import { countingVuexGetters, CountingSchemes } from '@ahaslides-product/frontend-toolkit'
import {
  isFeatureEnabled,
  isPresentationCreatedAfterTurnOnFlag,
  isPresentationResetAfterTurnOnFlag,
  comparePresentationCreatedDateWithFlag,
} from '@/vuex/modules/featureToggles.module'
import config from '@/config'
import { isClonedFromOnboardingTemplate } from './onboardingTemplateClone'

const _getters = {
  ...countingVuexGetters,

  canUseKvSyncLiveStats: (_, __, rootState, rootGetters) => {
    if (!rootState?.presentation?.id) {
      return false
    }
    return isFeatureEnabled(rootGetters, 'kv-sync-emqx-for-live-stats') && (isPresentationCreatedAfterTurnOnFlag(rootGetters, 'kv-sync-emqx-for-live-stats') || isPresentationResetAfterTurnOnFlag(rootState.presentation, 'kv-sync-emqx-for-live-stats'))
  },
  // SYST-74: share/casting cold-load variant of `canUseKvSyncLiveStats`, which can't run
  // here because it short-circuits on `presentation.id` and the endpoint choice happens
  // before the deck loads. Answers the same flag/date gates from the already-fetched
  // flag-info, only for the share code asked about — a mismatch or missing gate falls
  // through to the unbounded endpoint, the safe default the v1 renderer needs. No fallback
  // to `canUseKvSyncLiveStats` on purpose: it reads stale `presentation` state. See PR #5108.
  canUseKvSyncLiveStatsForShareCode: (_, __, rootState, rootGetters) => (shareCode) => {
    const flagInfo = rootState?.featureToggles?.presentationFlagInfoByShareCode
    if (!shareCode || flagInfo?.shareCode !== shareCode) return false
    if (!flagInfo.createdAt && !flagInfo.resetTimeStamp) return false
    return isFeatureEnabled(rootGetters, 'kv-sync-emqx-for-live-stats') && (comparePresentationCreatedDateWithFlag(flagInfo, 'kv-sync-emqx-for-live-stats') || isPresentationResetAfterTurnOnFlag(flagInfo, 'kv-sync-emqx-for-live-stats'))
  },
  isClonedFromTemplateAndNotResetYet: (_, __, rootState) => {
    return isClonedFromOnboardingTemplate(rootState.presentation, rootState.presenterLocale, config.env) && !isPresentationResetAfterTurnOnFlag(rootState.presentation, 'kv-sync-emqx-for-live-stats')
  },
  canUseWordCloudEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUsePollEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseScaleEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseReactionEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseQuizLiveCountsEmqx: (_, getters) => {
    // Requires answer v2, since per-option counts only reach the pipeline when
    // quiz answers go through liveproxy /answer/create/v2.
    return getters.canUseKvSyncLiveStats
      && getters.canUseLiveStatsForQuizV2
  },

  pollVoteCount: (state, getters, rootState) => {
    if (getters.canUsePollEmqx) {
      return getters.answererCountPerQuestion(rootState.slideActive.id, 0, rootState.slideActive.version)
    }
    return rootState.slideActive.multipleCountAnswer
  },
  scaleVoteCount: (state, getters, rootState) => {
    if (getters.canUseScaleEmqx) {
      return (rootState?.slideActive?.SlideOptions?.length) ? (
        Math.ceil(
          getters.answerCountPerActivity(rootState.slideActive.id, rootState.slideActive.version) /
          rootState.slideActive.SlideOptions.length,
        ))
        : 0
    }
    return rootState.slideActive.multipleCountAnswer
  },
  wordCloudVoteCount: (state, getters, rootState, rootGetters) => {
    const slide = rootState.slideActive
    if (getters.canUseWordCloudEmqx) {
      const words = rootGetters['counting/group'](CountingSchemes.answerCountPerAnswerValue({
        slideId: slide.id,
        slideVersion: slide.version || 1,
        questionId: 0,
      })) || {}
      return Object.values(words).reduce((sum, count) => sum + Math.max(0, Number(count) || 0), 0)
    }
    return rootState.slideActive?.SlideOptions?.length
  },
  canUseWordCloudLiveProxy: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'liveproxy-submit-wordcloud')
  },
  canUseLiveStatsForQuizV2: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'livestats-quiz-submission-v2')
  },
  canRetireLiveApi: (_, getters, ___, rootGetters) => {
    return getters.canUseLiveStatsForQuizV2 && isFeatureEnabled(rootGetters, 'retire-live-api')
  },
  canGetReactionCountFromAhaSync: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canReadCategoriseHistoryFromCounting: (_, getters) => {
    return getters.canRetireLiveApi
      && getters.canUseKvSyncLiveStats
  },
  correctAnswerCountPerActivityV2: (_, __, ___, rootGetters) => (activityId, activityVersion) => {
    return rootGetters['counting/total'](CountingSchemes.correctAnswerCountPerActivity({
      slideId: activityId,
      slideVersion: activityVersion,
    })) ?? 0
  },
  incorrectAnswerCountPerActivityV2: (_, __, ___, rootGetters) => (activityId, activityVersion) => {
    return rootGetters['counting/total'](CountingSchemes.incorrectAnswerCountPerActivity({
      slideId: activityId,
      slideVersion: activityVersion,
    })) ?? 0
  },
  correctAnswerCountPerAnswerValueV2: (_, __, ___, rootGetters) => (
    activityId, activityVersion, questionId, answerValue,
  ) => {
    return rootGetters['counting/total'](CountingSchemes.correctAnswerCountPerAnswerValue({
      slideId: activityId,
      slideVersion: activityVersion,
      questionId,
      answerValue,
    })) ?? 0
  },
  incorrectAnswerCountPerAnswerValueV2: (_, __, ___, rootGetters) => (
    activityId, activityVersion, questionId, answerValue,
  ) => {
    return rootGetters['counting/total'](CountingSchemes.incorrectAnswerCountPerAnswerValue({
      slideId: activityId,
      slideVersion: activityVersion,
      questionId,
      answerValue,
    })) ?? 0
  },

  answerCountPerActivity: (_, __, ___, rootGetters) => (activityId, version) => {
    return rootGetters['counting/total'](CountingSchemes.answerCountPerActivity({
      slideId: activityId,
      slideVersion: version,
    })) ?? 0
  },

  answerCountPerAnswerValue: (_, __, ___, rootGetters) => (
    activityId, questionId, version, answerValue,
  ) => {
    return rootGetters['counting/total'](CountingSchemes.answerCountPerAnswerValue({
      slideId: activityId,
      slideVersion: version,
      questionId,
      answerValue,
    })) ?? 0
  },

  scoreCountPerAnswerValue: (_, __, ___, rootGetters) => (
    activityId, questionId, version, score,
  ) => {
    return rootGetters['counting/total'](CountingSchemes.answerCountPerAnswerValue({
      slideId: activityId,
      slideVersion: version,
      questionId,
      answerValue: score,
    })) ?? 0
  },

  scoreHistogramTotals: (_, __, ___, rootGetters) => (activityId, version) => {
    const rows = rootGetters['counting/group'](CountingSchemes.answerCountPerAnswerValue({
      slideId: activityId,
      slideVersion: version,
    }))
    const perQuestion = {}
    let sum = 0
    let count = 0
    Object.keys(rows || {}).forEach((segment) => {
      const parts = segment.split('/')
      if (parts.length !== 2) {
        return
      }
      const score = Number(parts[1])
      const rowCount = Number(rows[segment])
      if (!Number.isFinite(score) || !Number.isFinite(rowCount) || rowCount <= 0) {
        return
      }
      if (!perQuestion[parts[0]]) {
        perQuestion[parts[0]] = { sum: 0, count: 0 }
      }
      perQuestion[parts[0]].sum += score * rowCount
      perQuestion[parts[0]].count += rowCount
      sum += score * rowCount
      count += rowCount
    })
    return { perQuestion, sum, count }
  },

  averageScorePerQuestion: (_, getters) => (activityId, version, questionId) => {
    const entry = getters.scoreHistogramTotals(activityId, version).perQuestion[`${questionId}`]
    if (!entry || entry.count === 0) {
      return null
    }
    return entry.sum / entry.count
  },

  averageScorePerActivity: (_, getters) => (activityId, version) => {
    const { sum, count } = getters.scoreHistogramTotals(activityId, version)
    if (count === 0) {
      return null
    }
    return sum / count
  },

  canUnsubscribeLiveStatsOnDestroy: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-unsubscribe-kvsync')
  },
}

export default _getters
